/**
 * FinanzAssistentDrawer — „Finanz-Assistent" als Drawer (shadcn Sheet, von rechts).
 *
 * Zwei Bereiche:
 *   1. STATUS-BRIEFING (oben): deterministisch, direkt aus der DB (read-only,
 *      robuste Queries). Zeigt offene Aufgaben, Bank-/Buchhaltungsstand je Firma,
 *      offene Prüfpunkte, Regress-Summe und einen USt-Organschaft-Hinweis.
 *   2. CHAT (unten): schickt Fragen an die Supabase Edge Function `finanz-assistent`
 *      (Inhaber-only via JWT). Fällt sauber zurück, falls die Function noch nicht
 *      deployt ist oder kein OPENAI_API_KEY hinterlegt ist — das Briefing bleibt
 *      davon unberührt.
 *
 * Design: ruhig, slate/zinc, EIN emerald-Akzent, deutsch, keine Emojis.
 * Es werden keine DB-Migrationen ausgeführt; alle Abfragen sind fehlertolerant.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Sparkles, Loader2, ListChecks, Landmark, ClipboardCheck, Gavel,
  RefreshCw, Send, MessageSquare, AlertTriangle, Building2,
} from 'lucide-react';

// ============================================================
// Firma-Labels
// ============================================================

const FIRMA_LABEL: Record<string, string> = {
  sv_buero_koop: 'SV Büro',
  lcs: 'LCS',
  prolea_fzco: 'PROLEA FZCO',
  pv_gbr: 'PV GbR',
  immobilien: 'Immobilien',
  privat: 'Privat',
};

function firmaLabel(f: string | null | undefined): string {
  if (!f) return 'Firmenübergreifend';
  return FIRMA_LABEL[f] || f;
}

// ============================================================
// Helpers
// ============================================================

function toNum(v: unknown): number {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtEuro(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function isMissing(error: unknown): boolean {
  if (!error) return false;
  const e = error as { message?: string; code?: string };
  const msg = (e.message || '') + ' ' + (e.code || '');
  return (
    msg.includes('PGRST205') ||
    msg.includes('schema cache') ||
    msg.includes('relation') ||
    msg.includes('does not exist')
  );
}

// ============================================================
// Typen (Briefing)
// ============================================================

interface AufgabenBriefing {
  gesamt: number;
  proPrio: Record<string, number>;
  topTitel: string[];
}

interface FirmaStatus {
  firma: string | null;
  txOffen: number;
  txGesamt: number;
  belegeOffen: number;
  anStb: number;
}

interface PruefBriefing {
  ausgabenOhneBeleg: number;
  belegeOhneBuchung: number;
}

interface Briefing {
  aufgaben: AufgabenBriefing;
  status: FirmaStatus[];
  pruef: PruefBriefing;
  regressBelegteSumme: number;
  regressPositionen: number;
}

// ============================================================
// Daten-Layer (Briefing)
// ============================================================

async function ladeAufgaben(): Promise<AufgabenBriefing> {
  const leer: AufgabenBriefing = { gesamt: 0, proPrio: {}, topTitel: [] };
  try {
    const { data, error } = await supabase
      .from('finanz_aufgaben')
      .select('titel, prioritaet, status, faellig')
      .neq('status', 'erledigt')
      .limit(1000);
    if (error) throw error;
    const rows = (data as { titel: string | null; prioritaet: string | null }[]) || [];
    const proPrio: Record<string, number> = {};
    for (const r of rows) {
      const p = r.prioritaet || 'mittel';
      proPrio[p] = (proPrio[p] || 0) + 1;
    }
    // Wichtigste zuerst: hoch > mittel > niedrig.
    const rang: Record<string, number> = { hoch: 0, mittel: 1, niedrig: 2 };
    const topTitel = rows
      .slice()
      .sort((a, b) => (rang[a.prioritaet || 'mittel'] ?? 1) - (rang[b.prioritaet || 'mittel'] ?? 1))
      .slice(0, 5)
      .map((r) => r.titel || 'Ohne Titel');
    return { gesamt: rows.length, proPrio, topTitel };
  } catch (err) {
    if (!isMissing(err)) console.error('Briefing: Aufgaben laden fehlgeschlagen:', err);
    return leer;
  }
}

async function ladeStatus(): Promise<FirmaStatus[]> {
  try {
    const { data, error } = await supabase
      .from('v_buchhaltung_status')
      .select('*')
      .limit(50);
    if (error) throw error;
    const rows = (data as Record<string, unknown>[]) || [];
    return rows.map((r) => ({
      firma: (r.firma as string) ?? null,
      txOffen: toNum(r.tx_offen),
      txGesamt: toNum(r.tx_gesamt),
      belegeOffen: toNum(r.belege_nicht_gebucht),
      anStb: toNum(r.buchungen_an_stb),
    }));
  } catch (err) {
    if (!isMissing(err)) console.error('Briefing: Buchhaltungsstatus laden fehlgeschlagen:', err);
    return [];
  }
}

async function ladePruef(): Promise<PruefBriefing> {
  const leer: PruefBriefing = { ausgabenOhneBeleg: 0, belegeOhneBuchung: 0 };
  try {
    const [ab, bo] = await Promise.all([
      supabase.from('v_pruef_ausgaben_ohne_beleg').select('id').limit(20000),
      supabase.from('v_pruef_belege_ohne_buchung').select('id').limit(20000),
    ]);
    return {
      ausgabenOhneBeleg: ab.error ? 0 : (ab.data?.length ?? 0),
      belegeOhneBuchung: bo.error ? 0 : (bo.data?.length ?? 0),
    };
  } catch (err) {
    if (!isMissing(err)) console.error('Briefing: Prüfung laden fehlgeschlagen:', err);
    return leer;
  }
}

async function ladeRegress(): Promise<{ summe: number; positionen: number }> {
  try {
    const { data, error } = await supabase
      .from('stb_regress')
      .select('betrag, status')
      .limit(2000);
    if (error) throw error;
    const rows = (data as { betrag: unknown; status: string | null }[]) || [];
    const summe = rows.reduce(
      (s, r) => (String(r.status || '').startsWith('belegt') ? s + toNum(r.betrag) : s),
      0,
    );
    return { summe, positionen: rows.length };
  } catch (err) {
    if (!isMissing(err)) console.error('Briefing: Regress laden fehlgeschlagen:', err);
    return { summe: 0, positionen: 0 };
  }
}

async function ladeBriefing(): Promise<Briefing> {
  const [aufgaben, status, pruef, regress] = await Promise.all([
    ladeAufgaben(),
    ladeStatus(),
    ladePruef(),
    ladeRegress(),
  ]);
  return {
    aufgaben,
    status,
    pruef,
    regressBelegteSumme: regress.summe,
    regressPositionen: regress.positionen,
  };
}

// ============================================================
// UI-Bausteine
// ============================================================

function Karte({
  icon, titel, children,
}: {
  icon: React.ReactNode;
  titel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3.5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-slate-400">{icon}</span>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{titel}</h3>
      </div>
      {children}
    </div>
  );
}

function PrioZeile({ label, n }: { label: string; n: number }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold text-slate-900 tabular-nums">{n}</span>
    </div>
  );
}

// ============================================================
// Briefing-Bereich
// ============================================================

function BriefingBereich({
  briefing, loading, onReload,
}: {
  briefing: Briefing | null;
  loading: boolean;
  onReload: () => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!briefing) {
    return (
      <div className="text-sm text-slate-500 py-6 text-center">
        Briefing konnte nicht geladen werden.
      </div>
    );
  }

  const { aufgaben, status, pruef } = briefing;
  const prioRang: [string, string][] = [
    ['hoch', 'Hoch'],
    ['mittel', 'Mittel'],
    ['niedrig', 'Niedrig'],
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">Automatisches Briefing aus der Datenbank.</p>
        <button
          type="button"
          onClick={onReload}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-500 border border-slate-200 hover:bg-slate-50"
          title="Briefing neu laden"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Aktualisieren
        </button>
      </div>

      {/* Offene Aufgaben */}
      <Karte icon={<ListChecks className="w-4 h-4" />} titel="Offene Aufgaben">
        <div className="text-2xl font-bold text-slate-900 tabular-nums mb-2">{aufgaben.gesamt}</div>
        <div className="space-y-1">
          {prioRang.map(([key, label]) =>
            aufgaben.proPrio[key] ? (
              <PrioZeile key={key} label={label} n={aufgaben.proPrio[key]} />
            ) : null,
          )}
        </div>
        {aufgaben.topTitel.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              Wichtigste
            </div>
            <ul className="space-y-1">
              {aufgaben.topTitel.map((t, i) => (
                <li key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-emerald-500 flex-shrink-0" />
                  <span className="line-clamp-2">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Karte>

      {/* Bank-/Buchhaltungsstand je Firma */}
      <Karte icon={<Building2 className="w-4 h-4" />} titel="Bank- & Buchhaltungsstand">
        {status.length === 0 ? (
          <p className="text-xs text-slate-400">Keine Statusdaten verfügbar.</p>
        ) : (
          <div className="space-y-2.5">
            {status.map((s, i) => (
              <div key={i} className="text-sm">
                <div className="font-semibold text-slate-800">{firmaLabel(s.firma)}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  <span className={s.txOffen > 0 ? 'text-slate-700 font-medium' : ''}>
                    {s.txOffen} von {s.txGesamt} Transaktionen offen
                  </span>
                  {' · '}
                  {s.belegeOffen} Belege ungebucht
                  {' · '}
                  {s.anStb} an StB
                </div>
              </div>
            ))}
          </div>
        )}
      </Karte>

      {/* Prüfung */}
      <Karte icon={<ClipboardCheck className="w-4 h-4" />} titel="Prüfung / Vollständigkeit">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xl font-bold text-slate-900 tabular-nums">{pruef.ausgabenOhneBeleg}</div>
            <div className="text-xs text-slate-500">Ausgaben ohne Beleg</div>
          </div>
          <div>
            <div className="text-xl font-bold text-slate-900 tabular-nums">{pruef.belegeOhneBuchung}</div>
            <div className="text-xs text-slate-500">Belege ohne Buchung</div>
          </div>
        </div>
      </Karte>

      {/* Regress */}
      <Karte icon={<Gavel className="w-4 h-4" />} titel="Regress (Steuerberater)">
        <div className="text-xl font-bold text-slate-900 tabular-nums">
          {fmtEuro(briefing.regressBelegteSumme)}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          Belegte Schadenssumme über {briefing.regressPositionen} Position(en)
        </div>
      </Karte>

      {/* USt-Organschaft-Hinweis */}
      <div className="flex items-start gap-2.5 bg-slate-50 border border-slate-200 rounded-xl p-3">
        <Landmark className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-slate-500 leading-relaxed">
          <span className="font-medium text-slate-600">USt-Organschaft (Entwurf):</span>{' '}
          SV Büro und LCS werden als umsatzsteuerliche Organschaft geführt — die USt wird
          konsolidiert über den Organträger abgeführt. Details unter „Alle → USt-Organschaft".
          Arbeitsstand, keine Steuerberatung.
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Chat-Bereich
// ============================================================

interface ChatNachricht {
  role: 'user' | 'assistant';
  content: string;
}

function ChatBereich() {
  const [nachrichten, setNachrichten] = useState<ChatNachricht[]>([]);
  const [eingabe, setEingabe] = useState('');
  const [senden, setSenden] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const endeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endeRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [nachrichten, senden]);

  const abschicken = async () => {
    const frage = eingabe.trim();
    if (!frage || senden) return;
    setFehler(null);
    setEingabe('');
    const neueNachrichten: ChatNachricht[] = [...nachrichten, { role: 'user', content: frage }];
    setNachrichten(neueNachrichten);
    setSenden(true);
    try {
      const { data, error } = await supabase.functions.invoke('finanz-assistent', {
        body: { frage, verlauf: nachrichten.slice(-8) },
      });
      if (error) throw error;
      const antwort = (data as { antwort?: string; error?: string; hinweis?: string }) || {};
      if (antwort.error) {
        setFehler(antwort.hinweis || antwort.error);
      } else {
        setNachrichten((prev) => [
          ...prev,
          { role: 'assistant', content: antwort.antwort || 'Keine Antwort erhalten.' },
        ]);
      }
    } catch (err) {
      console.error('Finanz-Assistent Chat fehlgeschlagen:', err);
      setFehler(
        'Der Chat ist derzeit nicht erreichbar. Möglicherweise ist die Funktion noch nicht ' +
        'freigeschaltet. Das Briefing oben funktioniert unabhängig davon.',
      );
    } finally {
      setSenden(false);
    }
  };

  const beiTaste = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      abschicken();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <MessageSquare className="w-4 h-4 text-slate-400" />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Fragen zu allen Finanzdaten
        </h3>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
        {nachrichten.length === 0 && !fehler && (
          <div className="text-xs text-slate-400 leading-relaxed bg-slate-50 border border-slate-200 rounded-xl p-3">
            Stelle eine Frage, z. B. „Welche Firma hat die meisten offenen Buchungen?" oder
            „Wie hoch ist die belegte Regress-Summe?". Der Assistent antwortet auf Basis der
            aktuellen Finanzdaten aller Betriebe.
          </div>
        )}

        {nachrichten.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-100 text-slate-800 border border-slate-200'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}

        {senden && (
          <div className="flex justify-start">
            <div className="rounded-xl px-3 py-2 bg-slate-100 border border-slate-200">
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            </div>
          </div>
        )}

        {fehler && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <span className="text-xs text-amber-700 leading-relaxed">{fehler}</span>
          </div>
        )}

        <div ref={endeRef} />
      </div>

      <div className="flex-shrink-0 pt-3 mt-1 border-t border-slate-100">
        <div className="flex items-end gap-2">
          <textarea
            value={eingabe}
            onChange={(e) => setEingabe(e.target.value)}
            onKeyDown={beiTaste}
            placeholder="Frage zu den Finanzen…"
            rows={2}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400 resize-none"
          />
          <button
            type="button"
            onClick={abschicken}
            disabled={senden || !eingabe.trim()}
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex-shrink-0"
            title="Senden"
          >
            {senden ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">
          Antworten basieren nur auf den hinterlegten Daten. Keine Steuerberatung.
        </p>
      </div>
    </div>
  );
}

// ============================================================
// Hauptkomponente
// ============================================================

export default function FinanzAssistentDrawer({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [loading, setLoading] = useState(true);

  const lade = useCallback(async () => {
    setLoading(true);
    const b = await ladeBriefing();
    setBriefing(b);
    setLoading(false);
  }, []);

  // Nur laden, wenn der Drawer geöffnet wird.
  useEffect(() => {
    if (open) lade();
  }, [open, lade]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col bg-slate-50/60"
      >
        <SheetHeader className="px-4 pt-4 pb-3 border-b border-slate-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <SheetTitle className="text-base font-bold text-slate-900">Finanz-Assistent</SheetTitle>
              <SheetDescription className="text-xs text-slate-400">
                Status-Briefing und Fragen zu allen Finanzdaten
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Briefing (scrollbar) */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
          <BriefingBereich briefing={briefing} loading={loading} onReload={lade} />
        </div>

        {/* Chat (feste Höhe unten) */}
        <div className="flex-shrink-0 border-t border-slate-200 bg-white px-4 py-3 h-[46%] min-h-[300px]">
          <ChatBereich />
        </div>
      </SheetContent>
    </Sheet>
  );
}
